import { Fragment } from "react";
import { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { Spinner } from "react-bootstrap";
import { axiosAuth } from "./api/axios";
import axios from "axios"; // plain axios, no auth header

const LOGIN_URL = "/accounts/auth/jwt/create/";
const VERIFY_OTP_URL = `${process.env.REACT_APP_API}accounts/auth/jwt/verify-otp/`;

const Login = () => {
  const { setAuth } = useAuth();
  console.log("1. In Login");
  const navigate = useNavigate();
  const location = useLocation();
  let from = location.state?.from?.pathname || "/";
  if (from === "/logout") {
    from = "/";
  }

  const userRef = useRef();
  const errRef = useRef();
  const otpRef = useRef();

  const [user, setUser] = useState("");
  const [pwd, setPwd] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [step, setStep] = useState("credentials"); // "credentials" | "otp"
  const [tempToken, setTempToken] = useState("");
  const [otp, setOtp] = useState("");

  let accessToken = "";

  useEffect(() => {
    console.log("2. In useEffect");
    userRef.current.focus();
    document.body.classList.add("accountBodyColor");
    return () => {
      document.body.classList.remove("accountBodyColor");
    };
  }, []);

  useEffect(() => {
    console.log("3. In useEffect");
    setErrMsg("");
  }, [user, pwd, otp]);

  // Focus OTP input when step changes
  useEffect(() => {
    console.log("4. In useEffect");
    if (step === "otp") {
      otpRef.current?.focus();
    }
  }, [step]);

  // Step 1: Submit credentials
  // Focus the error message once it appears, after React has committed the DOM node
  useEffect(() => {
    if (errMsg) {
      errRef.current?.focus();
    }
  }, [errMsg]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    console.log("5. InhandleSubmit");
    try {
      console.log("6. InhandleSubmit-try-b");
      const response = await axiosAuth.post(
        LOGIN_URL,
        { username: user, password: pwd },
        { withCredentials: true },
      );
      console.log("7. InhandleSubmit-a");
      // Store temp_token and move to OTP step
      setTempToken(response.data.temp_token);
      setStep("otp");
    } catch (err) {
      console.log("8. InhandleSubmit Error: ", err);

      const errorData = err.response?.data;

      // Safely extract the detail message whether it's a string, array, or object
      let errorDetail = "";
      if (typeof errorData === "string") {
        errorDetail = errorData;
      } else if (errorData?.detail) {
        errorDetail = Array.isArray(errorData.detail)
          ? errorData.detail.join(" ")
          : errorData.detail;
      }

      if (!err?.response) {
        setErrMsg("אין  תגובה מהשרת.");
      } else if (errorDetail.includes("Too many failed login attempts")) {
        setErrMsg("בוצעו יותר מדי ניסיונות כניסה שגויים. החשבון ננעל זמנית ל-5 דקות.");
      } else if (err.response?.status === 400) {
        setErrMsg("חסרים שם המשתמש או הסיסמה, או שפרטי ההתחברות שגויים.");
      } else if (err.response?.status === 403) {
        setErrMsg(
          "תוקף הסיסמה הסתיים, נא להחליף את הסיסמה, דרך לחיצה על הכפתור איפוס סיסמה.",
        );
      } else {
        setErrMsg("הכניסה למערכת נכשלה.");
      }

    } finally {
      setLoading(false);
    }
  };

  // Step 2: Submit OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        VERIFY_OTP_URL,
        { temp_token: tempToken, otp },
        { withCredentials: true },
      );

      if (response?.data.status !== 403) {
        const refreshToken = response?.data?.refresh;
        accessToken = response?.data?.access;
        const user_id = response?.data?.id;
        const roles = response?.data?.groups.map((role) =>
          role.replace(/,$/, ""),
        );

        setAuth({ user, pwd, accessToken, refreshToken, user_id, roles });
        setUser("");
        setPwd("");
        setOtp("");

        navigate(from, { replace: true });
      } else {
        setErrMsg(
          "תוקף הסיסמה הסתיים, נא להחליף את הסיסמה, דרך לחיצה על הכפתור איפוס סיסמה.",
        );
      }
    } catch (err) {
      if (!err?.response) {
        setErrMsg("אין תגובה מהשרת.");
      } else if (err.response?.status === 400) {
        setErrMsg("קוד שגוי או פג תוקף, נסה/י שנית.");
      } else {
        setErrMsg("האימות נכשל.");
      }
      errRef.current.focus();
    } finally {
      setLoading(false);
    }
  };

  // Go back to credentials step
  const handleBack = () => {
    setStep("credentials");
    setTempToken("");
    setOtp("");
    setErrMsg("");
  };

  if (loading) {
    return (
      <Fragment>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "100vh" }}
        >
          <div className="text-center">
            <Spinner animation="border" variant="danger" visible />
            <br />
            <br />
            <h3 className="spinnerText">
              {step === "otp"
                ? "מאמתים את הקוד, אנא המתן/י..."
                : "מאמתים את הנתונים, אנא המתן/י..."}
            </h3>
          </div>
        </div>
      </Fragment>
    );
  }

  // Step 1: Credentials form
  if (step === "credentials") {
    return (
      <Fragment>
        <center>
          <p
            ref={errRef}
            className={errMsg ? "errmsg" : "offscreen"}
            aria-live="assertive"
          >
            {errMsg}
          </p>
          <h1>כניסה</h1>
          <form className="accountForm" onSubmit={handleSubmit}>
            <label htmlFor="username">שם משתמש-ת"ז-:</label>
            <input
              type="text"
              id="username"
              ref={userRef}
              autoComplete="off"
              onChange={(e) => setUser(e.target.value)}
              value={user}
              required
            />
            <label htmlFor="password">סיסמה:</label>
            <input
              type="password"
              id="password"
              onChange={(e) => setPwd(e.target.value)}
              value={pwd}
              required
            />
            <button>כניסה</button>
          </form>
          <p>
            <label>הגדרת חשבון חדש?</label>
            <br />
            <span className="line">
              <a href="/register">רישום</a>
            </span>
          </p>
          <p>
            <label>שכחתי סיסמה</label>
            <br />
            <span className="line">
              <a href="/resetpassword">איפוס סיסמה</a>
            </span>
          </p>
        </center>
      </Fragment>
    );
  }

  // Step 2: OTP form
  return (
    <Fragment>
      <center>
        <p
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive"
        >
          {errMsg}
        </p>
        <h1>אימות דו-שלבי</h1>
        <p className="blinking-text" style={{ fontSize: "1.5rem" }}>
          <strong>
            קוד אימות ישלח לכתובת המייל שלך תוך מספר רגעים. הקוד תקף ל-2 דקות.
            אם לא מצאת את המייל, חפש בתיקיית ה Spam.
          </strong>
        </p>
        <form className="accountForm" onSubmit={handleVerifyOtp}>
          <label htmlFor="otp">קוד אימות:</label>
          <input
            type="text"
            id="otp"
            ref={otpRef}
            autoComplete="off"
            onChange={(e) => setOtp(e.target.value)}
            value={otp}
            maxLength={6}
            required
          />
          <button>אימות</button>
        </form>
        <p>
          <button
            onClick={handleBack}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            חזרה לכניסה
          </button>
        </p>
      </center>
    </Fragment>
  );
};

export default Login;
